import type { APIResponse } from '@yourownmetric/shared'

import { apiSlice } from './apiSlice'

// ── Types ────────────────────────────────────────────────────

export interface PlatformStats {
    tenants: number
    users: number
    projects: number
    totalMonthlyEvents: number
    activeSubscriptions: number
    recentSignups30d: number
    planBreakdown: { plan: string; count: number }[]
}

export interface AdminTenantRow {
    id: string
    name: string
    slug: string
    plan: string
    status: string
    region: string
    monthlyEventCount: number
    dataRetentionMonths: number
    stripeCustomerId: string | null
    razorpayCustomerId: string | null
    createdAt: string
    updatedAt: string
    memberCount: number
    projectCount: number
}

export interface AdminTenantDetail extends AdminTenantRow {
    aiAddon: number
    members: {
        id: string
        email: string
        name: string
        role: string
        createdAt: string
        lastLoginAt: string | null
    }[]
    projects: {
        id: string
        name: string
        domain: string
        timezone: string
        createdAt: string
    }[]
    subscription: {
        id: string
        provider: string
        plan: string
        status: string
        currentPeriodStart: string
        currentPeriodEnd: string
        createdAt: string
    } | null
}

export interface AdminUserRow {
    id: string
    email: string
    name: string
    emailVerified: number
    mfaEnabled: number
    isSuperadmin: number
    createdAt: string
    lastLoginAt: string | null
    tenantCount: number
    tenantNames: string | null
}

export interface AdminUserDetail {
    id: string
    email: string
    name: string
    emailVerified: number
    mfaEnabled: number
    isSuperadmin: number
    forcePasswordChange: number
    createdAt: string
    updatedAt: string
    lastLoginAt: string | null
    memberships: {
        tenantId: string
        role: string
        tenantName: string
        slug: string
        plan: string
        status: string
    }[]
}

export interface AdminSubscriptionRow {
    id: string
    tenantId: string
    provider: string
    providerSubscriptionId: string
    plan: string
    status: string
    currentPeriodStart: string
    currentPeriodEnd: string
    createdAt: string
    updatedAt: string
    tenantName: string
    tenantSlug: string
}

export interface AuditLogRow {
    id: string
    tenantId: string
    userId: string
    action: string
    resourceType: string
    resourceId: string
    metadata: string | null
    ipAddress: string | null
    createdAt: string
    userEmail: string | null
    tenantName: string | null
}

interface Paginated<T> {
    pagination: { page: number; limit: number; total: number; totalPages: number }
    items: T[]
}

export interface OrgOption {
    id: string
    shortId: string
    name: string
    slug: string
    plan: string
    status: string
    owners: string[]
    memberCount: number
    projects: { id: string; name: string; domain: string }[]
}

export interface UserOption {
    id: string
    shortId: string
    name: string
    email: string
    orgs: { id: string; name: string; role: string }[]
}

// ── Admin API Endpoints ──────────────────────────────────────

export const adminApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        // ── Pickers ──
        lookupOrgs: builder.query<OrgOption[], string>({
            query: q => `/admin/lookup/orgs?q=${encodeURIComponent(q)}&limit=20`,
            transformResponse: (res: APIResponse<OrgOption[]>) => res.data ?? [],
            keepUnusedDataFor: 30,
        }),
        lookupUsers: builder.query<UserOption[], string>({
            query: q => `/admin/lookup/users?q=${encodeURIComponent(q)}&limit=20`,
            transformResponse: (res: APIResponse<UserOption[]>) => res.data ?? [],
            keepUnusedDataFor: 30,
        }),

        // ── Platform Stats ──
        getAdminStats: builder.query<PlatformStats, void>({
            query: () => '/admin/stats',
            transformResponse: (res: APIResponse<PlatformStats>) => res.data!,
            providesTags: ['Admin'],
        }),

        // ── Tenants ──
        getAdminTenants: builder.query<
            Paginated<AdminTenantRow>,
            { page?: number; limit?: number; search?: string; status?: string; plan?: string }
        >({
            query: ({ page = 1, limit = 20, search, status, plan } = {}) => {
                const params = new URLSearchParams({
                    page: String(page),
                    limit: String(limit),
                })
                if (search) {
                    params.set('search', search)
                }
                if (status) {
                    params.set('status', status)
                }
                if (plan) {
                    params.set('plan', plan)
                }
                return `/admin/tenants?${params}`
            },
            transformResponse: (
                res: APIResponse<{
                    tenants: AdminTenantRow[]
                    pagination: Paginated<never>['pagination']
                }>,
            ) => ({
                items: res.data!.tenants,
                pagination: res.data!.pagination,
            }),
            providesTags: ['Admin'],
        }),

        getAdminTenant: builder.query<AdminTenantDetail, string>({
            query: id => `/admin/tenants/${id}`,
            transformResponse: (res: APIResponse<AdminTenantDetail>) => res.data!,
            providesTags: ['Admin'],
        }),

        updateAdminTenant: builder.mutation<
            { id: string; updated: boolean },
            {
                id: string
                name?: string
                plan?: string
                status?: string
                dataRetentionMonths?: number
                monthlyEventCount?: number
                aiAddon?: boolean
                /** Why (kept in the org's audit log as admin.org.*; customers never see it). */
                reason?: string
                /** Takedown / grievance register reference, e.g. GRV-20261102-01. */
                ticketRef?: string
            }
        >({
            query: ({ id, ...body }) => ({
                url: `/admin/tenants/${id}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: APIResponse<{ id: string; updated: boolean }>) => res.data!,
            invalidatesTags: ['Admin'],
        }),

        deleteAdminTenant: builder.mutation<
            { id: string; deleted: boolean },
            { id: string; hard?: boolean }
        >({
            query: ({ id, hard }) => ({
                url: `/admin/tenants/${id}${hard ? '?hard=true' : ''}`,
                method: 'DELETE',
            }),
            transformResponse: (res: APIResponse<{ id: string; deleted: boolean }>) => res.data!,
            invalidatesTags: ['Admin'],
        }),

        // ── Users ──
        getAdminUsers: builder.query<
            Paginated<AdminUserRow>,
            { page?: number; limit?: number; search?: string }
        >({
            query: ({ page = 1, limit = 20, search } = {}) => {
                const params = new URLSearchParams({
                    page: String(page),
                    limit: String(limit),
                })
                if (search) {
                    params.set('search', search)
                }
                return `/admin/users?${params}`
            },
            transformResponse: (
                res: APIResponse<{
                    users: AdminUserRow[]
                    pagination: Paginated<never>['pagination']
                }>,
            ) => ({
                items: res.data!.users,
                pagination: res.data!.pagination,
            }),
            providesTags: ['Admin'],
        }),

        getAdminUser: builder.query<AdminUserDetail, string>({
            query: id => `/admin/users/${id}`,
            transformResponse: (res: APIResponse<AdminUserDetail>) => res.data!,
            providesTags: ['Admin'],
        }),

        updateAdminUser: builder.mutation<
            { id: string; updated: boolean },
            {
                id: string
                name?: string
                isSuperAdmin?: boolean
                forcePasswordChange?: boolean
                emailVerified?: boolean
            }
        >({
            query: ({ id, ...body }) => ({
                url: `/admin/users/${id}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: APIResponse<{ id: string; updated: boolean }>) => res.data!,
            invalidatesTags: ['Admin'],
        }),

        deleteAdminUser: builder.mutation<{ id: string; deleted: boolean }, string>({
            query: id => ({
                url: `/admin/users/${id}`,
                method: 'DELETE',
            }),
            transformResponse: (res: APIResponse<{ id: string; deleted: boolean }>) => res.data!,
            invalidatesTags: ['Admin'],
        }),

        resetUserPassword: builder.mutation<
            { id: string; passwordReset: boolean },
            { userId: string; newPassword: string; forceChange?: boolean }
        >({
            query: ({ userId, ...body }) => ({
                url: `/admin/users/${userId}/reset-password`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: APIResponse<{ id: string; passwordReset: boolean }>) =>
                res.data!,
            invalidatesTags: ['Admin'],
        }),

        // ── Subscriptions ──
        getAdminSubscriptions: builder.query<
            Paginated<AdminSubscriptionRow>,
            { page?: number; limit?: number; status?: string }
        >({
            query: ({ page = 1, limit = 20, status } = {}) => {
                const params = new URLSearchParams({
                    page: String(page),
                    limit: String(limit),
                })
                if (status) {
                    params.set('status', status)
                }
                return `/admin/subscriptions?${params}`
            },
            transformResponse: (
                res: APIResponse<{
                    subscriptions: AdminSubscriptionRow[]
                    pagination: Paginated<never>['pagination']
                }>,
            ) => ({
                items: res.data!.subscriptions,
                pagination: res.data!.pagination,
            }),
            providesTags: ['Admin'],
        }),

        updateAdminSubscription: builder.mutation<
            { id: string; updated: boolean },
            {
                id: string
                plan?: string
                status?: string
                currentPeriodEnd?: string
            }
        >({
            query: ({ id, ...body }) => ({
                url: `/admin/subscriptions/${id}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: APIResponse<{ id: string; updated: boolean }>) => res.data!,
            invalidatesTags: ['Admin'],
        }),

        createAdminSubscription: builder.mutation<
            { id: string; tenantId: string; plan: string; status: string },
            {
                tenantId: string
                plan: string
                periodEnd: string
                monthlyEventLimit?: number
                note?: string
            }
        >({
            query: body => ({
                url: '/admin/subscriptions',
                method: 'POST',
                body,
            }),
            transformResponse: (
                res: APIResponse<{ id: string; tenantId: string; plan: string; status: string }>,
            ) => res.data!,
            invalidatesTags: ['Admin'],
        }),

        // ── Audit Logs ──
        getAuditLogs: builder.query<
            Paginated<AuditLogRow>,
            { page?: number; limit?: number; tenantId?: string; userId?: string }
        >({
            query: ({ page = 1, limit = 50, tenantId, userId } = {}) => {
                const params = new URLSearchParams({
                    page: String(page),
                    limit: String(limit),
                })
                if (tenantId) {
                    params.set('tenantId', tenantId)
                }
                if (userId) {
                    params.set('userId', userId)
                }
                return `/admin/audit-logs?${params}`
            },
            transformResponse: (
                res: APIResponse<{
                    logs: AuditLogRow[]
                    pagination: Paginated<never>['pagination']
                }>,
            ) => ({
                items: res.data!.logs,
                pagination: res.data!.pagination,
            }),
            providesTags: ['Admin'],
        }),
    }),
})

export const {
    useGetAdminStatsQuery,
    useGetAdminTenantsQuery,
    useGetAdminTenantQuery,
    useUpdateAdminTenantMutation,
    useDeleteAdminTenantMutation,
    useGetAdminUsersQuery,
    useGetAdminUserQuery,
    useUpdateAdminUserMutation,
    useDeleteAdminUserMutation,
    useResetUserPasswordMutation,
    useGetAdminSubscriptionsQuery,
    useUpdateAdminSubscriptionMutation,
    useCreateAdminSubscriptionMutation,
    useGetAuditLogsQuery,
    useLookupOrgsQuery,
    useLookupUsersQuery,
} = adminApi
